import { Trans, useLingui } from '@lingui/react/macro';
import {
  ArrowRight,
  ArrowRightUp,
  Desktop,
  DeviceMobile,
  Import,
  LayoutLaying,
} from '@remarkable/ark-icons';
import { Tag, Typography } from '@remarkable/ark-web';
import QRCode from 'react-qr-code';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import { getMobileLink } from 'src/apps/device/utils/utils';
import methodsDownloadApps from 'src/assets/img/methods-download-apps.webp';
import { LinkExternal } from 'src/components/LinkExternal';
import { defaultConfig } from 'src/config';
import { URLS } from 'src/utils/urls/urls';

import { AppPage } from './AppPage';
import { Button } from './Button';
import { DesktopAppLink } from './DesktopAppLink';
import { Divider } from './Divider';
import { Sheet } from './Sheet';

interface DownloadAppsProps {
  to: string;
}

export const DownloadApps = ({ to }: DownloadAppsProps) => {
  const { t } = useLingui();
  const mobileAppRedirectUrl = new URL(
    URLS.DOWNLOAD_MOBILE_APP_REDIRECT,
    window.location.href
  );

  return (
    <AppPage.Container>
      <AppPage.Content className="max-w-ll pt-24 lm:pt-64">
        <Sheet className="flex flex-col gap-32 text-left">
          <div className="flex flex-col gap-24">
            <Typography variant="heading-md">
              <Trans>Take your thinking further</Trans>
            </Typography>
            <Typography variant="body-md">
              <Trans>
                Kick-start your next task with new templates from reMarkable
                Methods, and work conveniently across your devices with our
                apps. Time to get even more out of your workday with Connect.
              </Trans>
            </Typography>
          </div>
          <Divider />
          <div className="flex flex-row gap-64">
            <div className="flex basis-full flex-col gap-16 lxl:basis-[65%]">
              <Tag variant="success-light" size="medium">
                <Trans>NEW TEMPLATES</Trans>
              </Tag>
              <div className="flex flex-row flex-nowrap items-center gap-16">
                <LayoutLaying size={32} />
                <Typography variant="heading-subtle-2xl">
                  <Trans>reMarkable Methods</Trans>
                </Typography>
              </div>
              <Typography variant="body-sm">
                <Trans>
                  Lead meetings, brainstorm, and plan your day with a new
                  collection of exclusive templates.
                </Trans>
              </Typography>
              <LinkExternal
                to={defaultConfig.MethodsUrl}
                componentLocation={ComponentLocations.SETUP.DOWNLOAD_APPS}
                className="border-b border-transparent font-medium text-pen-blue no-underline hover:border-pen-blue hover:no-underline"
              >
                <span className="inline-flex items-center gap-8">
                  <Trans>Get started here</Trans>
                  <ArrowRightUp
                    className="!h-16 !w-16 !transform-none"
                    size={16}
                  />
                </span>
              </LinkExternal>
            </div>
            <div className="hidden basis-[45%] flex-col justify-center lxl:flex">
              <img
                src={methodsDownloadApps}
                alt={t`A screen capture of the Methods homepage`}
                className="mx-auto w-full max-w-full min-w-[272px] lxl:w-auto"
              />
            </div>
          </div>

          <Divider />
          <div className="flex flex-col-reverse lm:flex-row">
            <div className="flex-col lm:flex lm:w-1/2 lm:border-r lm:pr-32">
              <div className="flex flex-row flex-nowrap items-center gap-16">
                <Desktop size={32} />
                <Typography variant="heading-subtle-2xl">
                  <Trans>Desktop app</Trans>
                </Typography>
              </div>

              <Typography className="mt-16 lm:mt-32" variant="body-sm">
                <Trans>
                  Create, transfer, and organize on your PC or Mac with the
                  desktop app.
                </Trans>
              </Typography>
              <div className="mt-24 flex flex-wrap gap-x-12 gap-y-8">
                <DesktopAppLink platform="macos" />
                <DesktopAppLink platform="windows" />
              </div>
            </div>
            <Divider className="my-32 lm:hidden" />
            <div className="flex flex-col lm:w-1/2 lm:pl-32">
              <div className="mt-0 flex flex-row flex-nowrap items-center gap-16">
                <DeviceMobile size={32} />
                <Typography variant="heading-subtle-2xl">
                  <Trans>Mobile app</Trans>
                </Typography>
              </div>

              <div className="mt-16 flex w-full flex-row justify-between gap-24 lm:mt-32">
                <div>
                  <Typography className="mt-0" variant="body-sm">
                    <Trans>
                      Capture thoughts on your phone, and develop them further
                      on your paper tablet.
                    </Trans>
                  </Typography>
                  <Typography
                    variant="body-sm"
                    className="mt-16 hidden lm:block"
                  >
                    <Trans>Scan the QR code to download</Trans>
                  </Typography>
                  <Button
                    as="a"
                    to={getMobileLink()}
                    variant="secondary"
                    size="medium"
                    className="mt-24 w-full lm:hidden"
                    analytics={{
                      location: ComponentLocations.SETUP.DOWNLOAD_APPS,
                      text: t`Download`,
                      action: 'download mobile app',
                    }}
                  >
                    <Import size={24} />
                    <span>
                      <Trans>Download</Trans>
                    </span>
                  </Button>
                </div>

                <QRCode
                  size={90}
                  value={mobileAppRedirectUrl.href}
                  className="hidden w-fit shrink-0 lm:block"
                />
              </div>
            </div>
          </div>
          <div className="mt-32 w-full">
            <Button
              as="a"
              to={to}
              variant="primary"
              className="float-right w-full lm:w-fit"
              analytics={{
                location: ComponentLocations.SETUP.DOWNLOAD_APPS,
                text: t`Next`,
                action: 'go to next step',
              }}
            >
              <span>
                <Trans>Next</Trans>
              </span>
              <ArrowRight />
            </Button>
          </div>
        </Sheet>
      </AppPage.Content>
    </AppPage.Container>
  );
};
